// Route `api` cho thực thể `job` (D-06): JSON của `/v1/jobs` (BOOKINGDATA, cột an toàn, không tiền) → `Job` của màn.
// Hub khai vào `API_ROUTES` (luồng A); màn không phải sửa. Trường worker chưa trả (flags, delayCount) để rỗng / 0.
import { isCargoType } from '@hana/domain';
import type { ApiRoute } from '@hana/data';
import type { Job, ShipmentStatus } from './types';

const STATUSES: readonly ShipmentStatus[] = ['RQ Request', 'RQ Standby', 'BK Standby', 'BK Confirmed', 'BL Hold', 'BL Wait RLS', 'BL Released', 'LS Job', 'No Show', 'Cancelled', 'RQ Cancel'];

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const orNull = (v: unknown): string | null => str(v) || null;
/** ISO từ worker → `YYYY-MM-DD` (đúng ngày trong chuỗi, không đổi múi giờ). */
const bool = (v: unknown): boolean | undefined => (v === true || v === 'Yes' || v === 1 ? true : v === false || v === 'No' || v === 0 ? false : undefined);
const day = (v: unknown): string | null => /^\d{4}-\d{2}-\d{2}/.exec(str(v))?.[0] ?? null;

/** Cờ cho 4 tab FRT / VED / ETA Pending / Chờ đóng; trường nào worker chưa trả thì bỏ (tab suy từ dữ liệu khác). */
function flagsFromApi(r: Row): Partial<Job> {
  const out: Partial<Job> = {};
  const bkcf = bool(r.BKCF), asOrder = bool(r.ASOrder), needVed = bool(r.NeedVED), debited = bool(r.debited ?? r.Debited);
  if (bkcf !== undefined) out.bkcf = bkcf;
  if (asOrder !== undefined) out.asOrder = asOrder;
  if (needVed !== undefined) out.needVed = needVed;
  if (debited !== undefined) out.debited = debited;
  if (r.VEDStatus !== undefined) out.vedStatus = orNull(r.VEDStatus);
  return out;
}

/** `flags` của worker ({ noq, task, dg, qdiff }: true / false / null) → mảng cờ của màn; chỉ lấy cờ true. */
function flagListFromApi(v: unknown): string[] {
  if (!v || typeof v !== 'object') return [];
  return ['noq', 'qdiff', 'dg', 'task'].filter((k) => (v as Row)[k] === true);
}

/** `services` của worker: [{ code, … }] → mã phí (sysServiceCode), bỏ trùng. */
function servicesFromApi(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return [...new Set(v.map((x) => str((x as Row | null)?.code)).filter(Boolean))];
}

export function jobFromApi(r: Row): Job {
  const status = str(r.ShipmentStatus);
  const type = str(r.TypeJob);
  const vol = str(r.Vol);
  const cont = str(r.TypeCont);
  const cargo = str(r.TypeCargo);
  return {
    id: str(r.JobID),
    dir: type === 'LS' || type === 'TR' ? type : 'OB',
    mode: str(r.TypeShipment) === 'AIR' ? 'AIR' : 'SEA',
    cargo: isCargoType(cargo) ? cargo : 'DC',
    customer: str(r.ShortName),
    shipper: str(r.SHIPPER),
    consignee: str(r.CNEE),
    shipment: str(r.TypeShipment),
    qty: vol && cont ? `${vol}×${cont}` : vol || cont,
    pol: str(r.PoLCode) || str(r.PoL),
    pod: str(r.PoDCode) || str(r.PoD),
    vessel: orNull(r.Vessel),
    carrier: orNull(r.CarrierCode) ?? orNull(r.Carrier),
    bookingNo: orNull(r.BookingNo),
    extraRefs: [str(r.HBL), str(r.MBL)].filter(Boolean),
    services: servicesFromApi(r.services),
    sale: str(r.SALENAME),
    cs: str(r.CUSNAME),
    bkg: orNull(r.PIC),
    status: (STATUSES as readonly string[]).includes(status) ? (status as ShipmentStatus) : 'RQ Request',
    close: r.CloseJob === true ? 'Locked' : null,
    flags: flagListFromApi(r.flags),
    commodity: str(r.Commodity),
    createdDay: Number(/^\d{4}-\d{2}-(\d{2})/.exec(str(r.JobDate))?.[1] ?? 0),
    etd: day(r.ETD),
    cyCutoff: orNull(r.CloseCY),
    eta: day(r.ETA),
    delayCount: Number(r.delayCount ?? 0) || 0,
    ...flagsFromApi(r),
  };
}

const items = (json: unknown): Row[] => {
  const list = (json as { items?: unknown } | null)?.items;
  return Array.isArray(list) ? (list as Row[]) : [];
};

/** Worker phân trang theo `page` + `limit`; ListQuery theo `offset` + `limit`. */
export const jobApiRoute: ApiRoute<Job> = {
  path: '/api/v1/jobs',
  query: (q) => {
    const limit = q.limit && q.limit > 0 ? q.limit : 50;
    return { q: q.q?.trim(), limit, page: Math.floor((q.offset ?? 0) / limit) + 1 };
  },
  parse: (json) => ({ items: items(json).map(jobFromApi), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
  parseOne: (json) => (json && typeof json === 'object' && 'JobID' in json ? jobFromApi(json as Row) : null),
};
